import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { usePredictionStore } from 'stores/predictionStore';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import PredictionsTable from '../components/PredictionsTable';
import PersonMultiSelectWithTooltips from '../components/PersonMultiSelectWithTooltips';
import GenreMultiSelect from '../components/GenreMultiSelect';
import MPAARatingSelect from '../components/MPAARatingSelect';
import AspectRatioSelect from '../components/AspectRatioSelect';
import CurrencyInput from '../components/CurrencyInput';
import FilmSearchInput from '../components/FilmSearchInput';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import AccountLimitsExceededTooltip from 'components/tooltips/AccountLimitsExceededTooltip';
import { TableRow, ColumnConfig, PersonRoleType, LoadDataType } from '../types';
import { Movie } from '../../../types/prediction-engine';
import { routes } from 'routes/routes';
import {
  predictionEnginePredict,
  predictionEngineLoadMovies,
} from 'api/prediction-engine';
import { LOAD_TABLE_FIELDS } from '../constants/table_fields';
import { usePredictionsEngineContext } from '../context/PredictionsEngineContext';
import { useStatistics } from 'queries/account';

import { handleSmartPaste, createEmptyRow } from '../utils/pasteUtils';
import { handleExport } from '../utils/exportUtils';
import { handleLoadData } from '../utils/tableDataUtils';
import { useFormValidation } from '../hooks';
import {
  CAST_LIMIT_ERROR_KEY,
  castLimitErrorKey,
} from '../hooks/useFormValidation';
import { usesLc2CastRule } from '../constants/castLimits';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  stashPendingPrediction,
  clearPendingPrediction,
} from '../utils/pendingPrediction';

// getError, when the column passes one in its cell props, returns the message
// the cell spells out for its row.
const createPersonMultiSelect = (roleType: PersonRoleType, fieldKey: string) =>
  React.memo(({ getError, ...props }: any) => (
    <PersonMultiSelectWithTooltips
      {...props}
      roleType={roleType}
      error={getError?.(props.rowIndex)}
    />
  ));

const ActorMultiSelect = createPersonMultiSelect(
  'actor',
  LOAD_TABLE_FIELDS.actors,
);
const DirectorMultiSelect = createPersonMultiSelect(
  'director',
  LOAD_TABLE_FIELDS.directors,
);
const WriterMultiSelect = createPersonMultiSelect(
  'writer',
  LOAD_TABLE_FIELDS.writers,
);
const ProducerMultiSelect = createPersonMultiSelect(
  'producer',
  LOAD_TABLE_FIELDS.producers,
);
const StarMultiSelect = createPersonMultiSelect(
  'star',
  LOAD_TABLE_FIELDS.stars,
);
const GenreMultiSelectContainer = React.memo((props: any) => (
  <GenreMultiSelect {...props} />
));
const CurrencyInputContainer = React.memo((props: any) => (
  <CurrencyInput {...props} />
));
const FilmSearchInputContainer = React.memo((props: any) => (
  <FilmSearchInput {...props} />
));
const DatePickerContainer = React.memo((props: any) => {
  const { value, onChange, rowIndex, column, row } = props;
  const { selectedModel } = usePredictionsEngineContext();
  const dateValue = value ? moment(value).toDate() : undefined;

  const isInvalid =
    typeof column.cell?.invalid === 'function'
      ? column.cell.invalid(rowIndex)
      : column.cell?.invalid;

  const minDate = selectedModel?.predicting_limit_date
    ? new Date(selectedModel.predicting_limit_date)
    : undefined;

  const tooltipContent = minDate
    ? `Minimum date: ${minDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })}`
    : 'Select release date';

  return (
    <div className="tw-relative">
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <DatePicker
              value={dateValue}
              onChange={(date) => {
                const dateString = date
                  ? moment(date).format('YYYY-MM-DD')
                  : '';
                onChange(dateString);
              }}
              placeholder="Select date"
              minDate={minDate}
              className={clsx(
                'tw-w-full tw-border tw-rounded-md tw-px-3 tw-py-2 tw-text-sm tw-shadow-sm tw-transition-all focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2',
                'tw-h-[2.5rem]',
                isInvalid &&
                  '!tw-border-red-500 focus-visible:!tw-ring-red-500',
              )}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
      {/* {isInvalid && (
        <span className="tw-absolute tw-top-[29px] tw-left-1 tw-text-xs tw-text-red-500 tw-mt-1">
          Invalid date
        </span>
      )} */}
    </div>
  );
});

const LoadRoute: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const { selectedModel } = usePredictionsEngineContext();
  const castRuleVersion = selectedModel?.cast_rule_version ?? null;
  const { savePrediction } = usePredictionStore();
  const { data: statistics } = useStatistics();

  const [tableData, setTableData] = useState<TableRow[]>([createEmptyRow()]);
  const [loadingRows, setLoadingRows] = useState<Set<number>>(new Set());
  const [highlightedRowIndex, setHighlightedRowIndex] = useState<number | null>(null);
  const [highlightedRowVariant, setHighlightedRowVariant] = useState<'amber' | 'green'>('amber');
  const processedMovieIdRef = useRef<string | null>(null);

  const [isDragActive, setIsDragActive] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef<number>(0);

  useEffect(() => {
    // The model decides how much of the cast comes back (LC2 models get it cut
    // to their limits). A film page opens this tab before the model list has
    // arrived, so wait for a model rather than load the full cast.
    if (!selectedModel) return;

    const searchParams = new URLSearchParams(location.search);
    const movieId = searchParams.get('movieId');

    if (movieId && movieId !== processedMovieIdRef.current) {
      processedMovieIdRef.current = movieId;
      const rowWithMovieId = createEmptyRow();
      rowWithMovieId.internal_id = movieId;
      const initialData = [rowWithMovieId];
      setTableData(initialData);

      handleLoadData(
        'Normal',
        initialData,
        setTableData,
        selectedModel.cast_rule_version ?? null,
      );
    }
  }, [location.search, selectedModel]);

  const { validationErrors, hasValidationErrors, hasEmptyRows, getFieldError, isRowValid } =
    useFormValidation(tableData, selectedModel);

  const handleLoadDataForRow = async (
    rowIndex: number,
    movieId: string,
  ): Promise<void> => {
    if (!movieId || movieId.trim() === '') return;

    setLoadingRows(prev => { const s = new Set(prev); s.add(rowIndex); return s; });
    const toastId = toast.loading('Loading movie data...');

    try {
      const response = await predictionEngineLoadMovies({
        movieIds: [movieId],
        limited: false,
        castRuleVersion,
      });

      if ('error' in response) {
        toast.error('Could not load movie data.', { id: toastId });
        console.error('Error loading movie:', response.error);
        return;
      }

      if (response.movies && response.movies.length > 0) {
        const loadedMovie = response.movies[0];
        const newData = [...tableData];
        newData[rowIndex] = {
          ...loadedMovie,
          version_id: null,
          is_custom: false,
          plot: loadedMovie.plot || loadedMovie.script?.name || '',
        } as TableRow;
        setTableData(newData);
        toast.success('Movie data loaded successfully!', { id: toastId });
      } else {
        toast.error('Could not load movie data.', { id: toastId });
      }
    } catch (error) {
      toast.error('Could not load movie data.', { id: toastId });
      console.error('Failed to load movie:', error);
    } finally {
      setLoadingRows(prev => { const s = new Set(prev); s.delete(rowIndex); return s; });
    }
  };

  const handleCellChange = (
    rowIndex: number,
    column: string,
    value: string | any[],
  ): void => {
    const newData = [...tableData];
    newData[rowIndex][column as keyof TableRow] = value;
    newData[rowIndex].is_custom = true;
    setTableData(newData);
  };

  const addNewRow = (): void => {
    const newIndex = tableData.length;
    setTableData([...tableData, createEmptyRow()]);
    setHighlightedRowIndex(newIndex);
    setHighlightedRowVariant('green');
    setTimeout(() => setHighlightedRowIndex(null), 1500);
    toast.success('Row added');
  };

  const addRowAtIndex = (index: number): void => {
    const newData = [...tableData];
    newData.splice(index, 0, createEmptyRow());
    setTableData(newData);
    setHighlightedRowIndex(index);
    setHighlightedRowVariant('green');
    setTimeout(() => setHighlightedRowIndex(null), 1500);
  };

  const deleteRow = (rowIndex: number): void => {
    if (tableData.length > 1) {
      const newData = tableData.filter((_, index) => index !== rowIndex);
      setTableData(newData);
      toast.success('Row removed');
    }
  };

  const duplicateRow = (rowIndex: number): void => {
    const newData = [...tableData];
    newData.splice(rowIndex + 1, 0, { ...tableData[rowIndex] });
    setTableData(newData);
    setHighlightedRowIndex(rowIndex + 1);
    setHighlightedRowVariant('amber');
    setTimeout(() => setHighlightedRowIndex(null), 1500);
    toast.success('Row duplicated');
  };

  const loadData = (type: LoadDataType): void => {
    handleLoadData(type, tableData, setTableData, castRuleVersion);
  };

  const handleReset = (): void => {
    setTableData([createEmptyRow()]);
  };

  const handleCsvFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (text) {
        handleSmartPaste(text, 0, undefined, tableData, setTableData);
      }
    };
    reader.readAsText(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCsvImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragActive(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (
        file.type === 'text/csv' ||
        file.name.endsWith('.csv') ||
        file.name.endsWith('.txt')
      ) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const text = event.target?.result as string;
          if (text) {
            handleSmartPaste(text, 0, undefined, tableData, setTableData);
          }
        };
        reader.readAsText(file);
      } else {
        toast.error('Please drop a CSV or TXT file');
      }
    }
  };

  const columns: ColumnConfig[] = [
    {
      key: LOAD_TABLE_FIELDS.internal_id,
      header: 'Internal ID',
      cell: {
        type: 'custom',
        component: FilmSearchInputContainer,
        invalid: (rowIndex: number) =>
          Boolean(getFieldError(rowIndex, LOAD_TABLE_FIELDS.internal_id)),
        props: {
          onFilmSelect: handleLoadDataForRow,
          isRowLoading: (rowIndex: number) => loadingRows.has(rowIndex),
        },
      },
      width: 350,
      resizable: true,
    },
    {
      key: LOAD_TABLE_FIELDS.version_id,
      header: 'Version ID',
      cell: { type: 'editable' },
      width: 190,
    },
    {
      key: LOAD_TABLE_FIELDS.title,
      header: 'Title',
      cell: {
        type: 'editable',
        invalid: (rowIndex: number) =>
          Boolean(getFieldError(rowIndex, LOAD_TABLE_FIELDS.title)),
      },
      width: 200,
      resizable: true,
    },
    {
      key: LOAD_TABLE_FIELDS.release_date,
      header: 'Release Date',
      cell: {
        type: 'custom',
        component: DatePickerContainer,
        invalid: (rowIndex: number) =>
          Boolean(getFieldError(rowIndex, LOAD_TABLE_FIELDS.release_date)),
      },
      width: 150,
    },
    {
      key: LOAD_TABLE_FIELDS.budget,
      header: 'Production Budget',
      cell: {
        type: 'custom',
        component: CurrencyInputContainer,
        invalid: (rowIndex: number) =>
          Boolean(getFieldError(rowIndex, LOAD_TABLE_FIELDS.budget)),
      },
      width: 145,
    },
    {
      key: LOAD_TABLE_FIELDS.actors,
      header: 'Actors',
      cell: {
        type: 'custom',
        component: ActorMultiSelect,
        invalid: (rowIndex: number) =>
          Boolean(getFieldError(rowIndex, LOAD_TABLE_FIELDS.actors)),
        props: {
          getError: (rowIndex: number) =>
            getFieldError(
              rowIndex,
              castLimitErrorKey(LOAD_TABLE_FIELDS.actors),
            ),
        },
      },
      width: 220,
      resizable: true,
    },
    {
      key: LOAD_TABLE_FIELDS.directors,
      header: 'Directors',
      cell: {
        type: 'custom',
        component: DirectorMultiSelect,
        invalid: (rowIndex: number) =>
          Boolean(getFieldError(rowIndex, LOAD_TABLE_FIELDS.directors)),
      },
      width: 220,
    },
    {
      key: LOAD_TABLE_FIELDS.writers,
      header: 'Writers',
      cell: {
        type: 'custom',
        component: WriterMultiSelect,
        invalid: (rowIndex: number) =>
          Boolean(getFieldError(rowIndex, LOAD_TABLE_FIELDS.writers)),
      },
      width: 220,
    },
    {
      key: LOAD_TABLE_FIELDS.producers,
      header: 'Producers',
      cell: {
        type: 'custom',
        component: ProducerMultiSelect,
        invalid: (rowIndex: number) =>
          Boolean(getFieldError(rowIndex, LOAD_TABLE_FIELDS.producers)),
      },
      width: 220,
    },
    {
      key: LOAD_TABLE_FIELDS.stars,
      header: 'Stars',
      cell: {
        type: 'custom',
        component: StarMultiSelect,
        invalid: (rowIndex: number) =>
          Boolean(getFieldError(rowIndex, LOAD_TABLE_FIELDS.stars)),
        props: {
          getError: (rowIndex: number) =>
            getFieldError(rowIndex, castLimitErrorKey(LOAD_TABLE_FIELDS.stars)),
        },
      },
      width: 220,
    },
    {
      key: LOAD_TABLE_FIELDS.plot,
      header: 'Plot',
      cell: { type: 'editable' },
      width: 250,
      minWidth: 200,
      maxWidth: 500,
      resizable: true,
    },
    {
      key: LOAD_TABLE_FIELDS.mpaa_rating,
      header: 'MPAA Rating',
      cell: {
        type: 'custom',
        component: MPAARatingSelect,
        invalid: (rowIndex: number) =>
          Boolean(getFieldError(rowIndex, LOAD_TABLE_FIELDS.mpaa_rating)),
      },
      width: 100,
    },
    {
      key: LOAD_TABLE_FIELDS.runtime,
      header: 'Runtime',
      cell: {
        type: 'editable',
        inputType: 'number',
        invalid: (rowIndex: number) =>
          Boolean(getFieldError(rowIndex, LOAD_TABLE_FIELDS.runtime)),
      },
      width: 100,
    },
    {
      key: LOAD_TABLE_FIELDS.aspect_ratio,
      header: 'Aspect Ratio',
      cell: {
        type: 'custom',
        component: AspectRatioSelect,
        invalid: (rowIndex: number) =>
          Boolean(getFieldError(rowIndex, LOAD_TABLE_FIELDS.aspect_ratio)),
      },
      width: 135,
    },
    {
      key: LOAD_TABLE_FIELDS.genres,
      header: 'Genres',
      cell: {
        type: 'custom',
        component: GenreMultiSelectContainer,
        invalid: (rowIndex: number) =>
          Boolean(getFieldError(rowIndex, LOAD_TABLE_FIELDS.genres)),
      },
      width: 185,
      resizable: true,
    },
  ];

  const buildPredictionRequest = () => ({
    movies: (tableData as Movie[]).map((movie) => ({
      ...movie,
      title: movie.title || null,
      internal_id: movie.internal_id || null,
      version_id: movie.version_id || null,
      pred_roi: movie.pred_roi || null,
      score: movie.score || null,
      release_date: movie.release_date || null,
      budget: movie.budget || null,
      plot: movie.plot?.trim() || null,
      mpaa_rating: movie.mpaa_rating || null,
      runtime: movie.runtime || null,
      script: movie.script || null,
      aspect_ratio: movie.aspect_ratio || null,
      is_custom: movie.is_custom === undefined ? true : movie.is_custom,
    })),
    model_id: selectedModel?.id || '',
  });

  const handleProcess = async () => {
    const resultsPath = `${routes.PREDICTIONS_ENGINE}/predict`;
    // is_export_allowed lives on the load rows rather than the response, so it
    // is merged back in by index once the prediction returns.
    const exportFlags = tableData.map((row) => row.is_export_allowed ?? false);

    // Mobile browsers only allow window.open while the click is still on the
    // stack, so the results tab cannot be opened after the request resolves.
    // Open it up front and hand the request over instead: the new tab does the
    // waiting and reports the outcome, while this tab keeps the entered rows.
    if (isMobile) {
      const pendingId = stashPendingPrediction({
        ...buildPredictionRequest(),
        export_flags: exportFlags,
      });

      // The id travels in the URL so only the tab opened for this request can
      // claim it, and this tab stays exactly as it is.
      if (
        pendingId &&
        window.open(`${resultsPath}?pending=${pendingId}`, '_blank')
      ) {
        toast.success('Running predictions in a new tab...');
        return;
      }

      // Storage was full or the tab was refused; run it here instead.
      clearPendingPrediction();
    }

    setIsProcessing(true);
    try {
      const response = await predictionEnginePredict(buildPredictionRequest());

      savePrediction({
        ...response,
        movies: response.movies?.map((movie: any, index: number) => ({
          ...movie,
          is_export_allowed: exportFlags[index] ?? false,
        })),
      });

      if (window.open(resultsPath, '_blank')) {
        toast.success('Prediction completed! Opening results in new tab...');
      } else {
        // Blocked, because the response outlived the click that allowed a
        // popup: Safari grants no grace at all, and Chrome only about five
        // seconds. The results are already saved, so offer them behind a
        // button — that tap is a fresh gesture, so the tab is allowed again.
        // Never navigate this tab there instead: that unmounts the load table
        // and the entered rows would have to be typed in again.
        toast(
          (t) => (
            <span className="tw-flex tw-items-center tw-gap-3">
              Predictions are ready.
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  toast.dismiss(t.id);
                  window.open(resultsPath, '_blank');
                }}
              >
                View results
              </Button>
            </span>
          ),
          { duration: 20000 },
        );
      }
    } catch (error: any) {
      if (Array.isArray(error?.message)) {
        error.message.forEach((msg: string) => toast.error(msg));
      } else {
        // The API rejects with { error, status }, so without this branch a
        // failed prediction showed nothing at all.
        toast.error(
          error?.error || error?.message || 'Failed to run predictions.',
        );
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const isExportAllowed = tableData.some(
    (row) => row.is_export_allowed === true,
  );

  const castLimitReason = Object.values(validationErrors)
    .map((rowErrors) => rowErrors[CAST_LIMIT_ERROR_KEY])
    .find(Boolean);

  const canCreatePrediction = statistics?.can_create_prediction ?? true;
  const isProcessButtonDisabled =
    hasValidationErrors ||
    hasEmptyRows ||
    !selectedModel ||
    isProcessing ||
    !canCreatePrediction;

  const actionButtons = [
    <Button key="csv-import" variant="default" onClick={handleCsvImportClick}>
      CSV Import
    </Button>,

    usesLc2CastRule(selectedModel) ? (
      // LC2 loads come back cut to the model's cast limits either way, so
      // there is no Normal/Limited choice to offer.
      <Button
        key="load-data"
        variant="default"
        onClick={() => loadData('Normal')}
      >
        Load Data
      </Button>
    ) : (
      <DropdownMenu key="load-data">
        <DropdownMenuTrigger>
          <Button variant="default">
            Load Data
            <ChevronDownIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="bottom" sideOffset={4}>
          <DropdownMenuItem onClick={() => loadData('Normal')}>
            Normal Load
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => loadData('Limited')}>
            Limited Load
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),

    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button variant="default" disabled={!isExportAllowed}>
          Export
          <ChevronDownIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="bottom" sideOffset={4}>
        <DropdownMenuItem
          onClick={() =>
            handleExport('CSV', tableData, columns, setIsExporting)
          }
          disabled={isExporting}
        >
          {isExporting ? 'Exporting...' : 'Export as CSV'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>,

    <Button key="reset" variant="secondary" onClick={handleReset}>
      Reset Data
    </Button>,

    (() => {
      const processTooltipContent = !canCreatePrediction ? (
        <AccountLimitsExceededTooltip />
      ) : hasEmptyRows ? (
        <span>Remove or fill in empty rows before processing</span>
      ) : castLimitReason ? (
        <span>{castLimitReason}</span>
      ) : null;

      return processTooltipContent ? (
        <div key="process-tooltip" className="tw-flex tw-flex-col tw-gap-1.5">
          <Tooltip>
            <TooltipTrigger>
              <Button
                key="process"
                variant="default"
                onClick={handleProcess}
                disabled={isProcessButtonDisabled}
              >
                {isProcessing ? 'Processing...' : 'Process'}
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="tw-bg-gray-900 tw-text-white tw-text-xs tw-max-w-[250px]"
            >
              {processTooltipContent}
            </TooltipContent>
          </Tooltip>
          {/* Tooltips need hover, so on phones the same reason is shown under the button. */}
          <div
            data-testid="process-disabled-reason"
            className="tw-rounded-md tw-bg-gray-900 tw-p-2 tw-text-xs tw-text-white md:tw-hidden"
          >
            {processTooltipContent}
          </div>
        </div>
      ) : (
        <Button
          key="process"
          variant="default"
          onClick={handleProcess}
          disabled={isProcessButtonDisabled}
        >
          {isProcessing ? 'Processing...' : 'Process'}
        </Button>
      );
    })(),
  ];

  return (
    <div
      className="tw-relative tw-h-full"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.txt"
        onChange={handleCsvFileImport}
        className="tw-hidden"
      />

      {isDragActive && (
        <div className="tw-fixed tw-inset-0 tw-z-[10000] tw-flex tw-items-center tw-justify-center tw-bg-black/50 tw-backdrop-blur-sm">
          <div className="tw-bg-background tw-rounded-lg tw-p-12 tw-shadow-lg tw-border-2 tw-border-dashed tw-border-primary tw-max-w-md tw-text-center tw-animate-in tw-fade-in-0 tw-zoom-in-95 tw-duration-200">
            <div className="tw-mb-6">
              <Upload className="tw-mx-auto tw-h-16 tw-w-16 tw-text-primary" />
            </div>
            <h3 className="tw-text-lg tw-font-semibold tw-mb-2">
              Drop CSV File Here
            </h3>
            <p className="tw-text-muted-foreground tw-text-sm">
              Release to import your CSV data into the table
            </p>
          </div>
        </div>
      )}

      <PredictionsTable
        actionButtons={actionButtons}
        columns={columns}
        tableData={tableData}
        onCellChange={handleCellChange}
        onAddRow={addNewRow}
        onDeleteRow={deleteRow}
        onDuplicateRow={duplicateRow}
        onInsertRow={addRowAtIndex}
        onPaste={(clipboardData, startRowIndex, focusedColumnKey) =>
          handleSmartPaste(
            clipboardData,
            startRowIndex,
            focusedColumnKey,
            tableData,
            setTableData,
          )
        }
        disableAddButton={false}
        isRowValid={isRowValid}
        hasValidationErrors={hasValidationErrors}
        highlightedRowIndex={highlightedRowIndex}
        highlightedRowVariant={highlightedRowVariant}
      />
    </div>
  );
};

export default LoadRoute;
